import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { UpgradeModal } from "@/components/UpgradeModal";
import { usePageMeta } from "@/hooks/usePageMeta";
import { PRICING as P } from "@/content/site";
import { Faq } from "@/components/home/Faq";

const TIERS = [
  {
    id: "free", name: "Free", price: "$0", unit: "", billing: "No card required",
    perks: [`${P.freeReadings} full readings, free`, "Daily Ritual card every day", "Up to 3 follow-up questions per saved reading", "Journal to save and revisit readings"],
    cta: "Start a free reading",
  },
  {
    id: "monthly", name: "Premium Monthly", price: P.monthly, unit: "/month", billing: "Billed monthly · cancel anytime",
    perks: [`${P.premiumReadings} full readings every month`, "Daily Ritual + up to 3 follow-up questions per reading", "Save, revisit & share every reading"],
    cta: "Choose monthly",
  },
  {
    id: "annual", name: "Premium Annual", price: P.annualPerMonth, unit: "/month", billing: `Billed ${P.annual} once a year · save ${P.annualSaving}`,
    perks: [`${P.premiumReadings} full readings every month`, "Daily Ritual + up to 3 follow-up questions per reading", "Save, revisit & share every reading"],
    cta: "Choose annual", recommended: true,
  },
];

const TierCard = ({ tier, onChoose }) => (
  <div
    data-testid={`pricing-tier-${tier.id}`}
    className={`relative flex flex-col rounded-2xl border p-6 ${tier.recommended ? "border-[#D9A441]/70 bg-[#D9A441]/10 shadow-[0_0_30px_rgba(217,164,65,0.15)]" : "border-[#D9A441]/20 bg-[#131422]/60"}`}
  >
    {tier.recommended && (
      <span className="absolute -top-3 left-6 rounded-full bg-[#D9A441] px-3 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#0B0C15]" data-testid="pricing-recommended-badge">
        Best value · recommended
      </span>
    )}
    <p className="text-xs uppercase tracking-[0.2em] text-[#A3A5B8]">{tier.name}</p>
    <p className="mt-2 flex items-baseline gap-1">
      <span className="font-display text-4xl font-semibold text-[#F8F3E6]">{tier.price}</span>
      <span className="text-sm text-[#A3A5B8]">{tier.unit}</span>
    </p>
    <p className="mt-1 text-xs text-[#E5C365]">{tier.billing}</p>
    <ul className="mt-5 flex-1 space-y-2">
      {tier.perks.map((p) => (
        <li key={p} className="flex gap-2 text-sm text-[#F8F3E6]"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#D9A441]" />{p}</li>
      ))}
    </ul>
    <button
      onClick={() => onChoose(tier.id)}
      data-testid={`pricing-cta-${tier.id}`}
      className={`mt-6 rounded-full px-5 py-3 text-sm font-medium transition-colors ${tier.recommended ? "bg-[#D9A441] text-[#0B0C15] hover:bg-[#E5C365]" : "border border-[#D9A441]/50 text-[#E5C365] hover:bg-[#D9A441]/10"}`}
    >
      {tier.cta}
    </button>
  </div>
);

export default function Pricing() {
  const navigate = useNavigate();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  usePageMeta({
    title: "Pricing - The Cardlight",
    description: `Start with ${P.freeReadings} free AI tarot readings, no card required. Premium is ${P.monthly}/month or ${P.annual}/year for ${P.premiumReadings} full readings a month. Cancel anytime.`,
    path: "/pricing",
  });
  const choose = (id) => (id === "free" ? navigate("/") : setUpgradeOpen(true));
  return (
    <div className="mx-auto max-w-5xl px-5 py-12 sm:px-8" data-testid="pricing-page">
      <h1 className="font-display text-4xl font-semibold text-[#F8F3E6] sm:text-5xl">Pricing</h1>
      <p className="mt-3 max-w-2xl text-base text-[#A3A5B8]">
        Your first {P.freeReadings} readings are free, no card required. After that, Premium gives you {P.premiumReadings} full readings a month.
      </p>
      <div className="mt-10 grid gap-6 md:grid-cols-3">
        {TIERS.map((t) => <TierCard key={t.id} tier={t} onChoose={choose} />)}
      </div>
      <p className="mt-6 text-center text-xs text-[#A3A5B8]">
        Prices in US dollars. Unused readings don't roll over. Secure checkout by Stripe.{" "}
        <Link to="/terms" className="text-[#E5C365] underline underline-offset-4" data-testid="pricing-terms-link">Subscription terms &amp; refunds</Link>
      </p>
      <Faq />
      <UpgradeModal open={upgradeOpen} onOpenChange={setUpgradeOpen} />
    </div>
  );
}
